import { useEffect, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import type { StoreTypeT } from '../../../../../common/types/types';
import { handleShortcutRegistrationError } from '../../../../../index';
import { SIDE_PANEL_ACTIONS } from '../../../../../modernization-common/side-panel/const';
import useHideShortcutKeyHandlers from '../../../../../modernization-common/side-panel/hooks/use-hide-shortcut-key-handlers';
import type { ModernizedGenericAnnotation, SidePanelActions } from '../../../../../modernization-common/side-panel/types';
import useShortcut from '../../../../labelling/providers/hooks/use-shortcut';
import { annotationLabelsAtom } from '../../../atoms/annotation-atoms';
import { annotationsAtom } from '../../../atoms/side-panel-atoms';
import { selectedAnnotationIdsAtom } from '../../../atoms/tool-atoms';
type PropsT = {
  dispatch: (action: SidePanelActions<ModernizedGenericAnnotation>) => void;
  onOpenTaskQuestionnaire?: () => void;
};
export const useSidePanelHotkeys = ({
  dispatch,
  onOpenTaskQuestionnaire
}: PropsT) => {
  const {
    registerHotKeys
  } = useShortcut();
  const selectedAnnotationIds = useAtomValue(selectedAnnotationIdsAtom);
  const annotationLabels = useAtomValue(annotationLabelsAtom);
  const annotations = useAtomValue(annotationsAtom);
  const selectedAnnotations = useMemo(() => {
    return annotations.filter(annotation => selectedAnnotationIds.includes(annotation.id));
  }, [selectedAnnotationIds, annotations]);
  const toggleShowHideAnnotations = (hidden: boolean, annotationId: string, storeType?: StoreTypeT) => {
    dispatch({
      type: SIDE_PANEL_ACTIONS.SHOW_HIDE_ANNOTATIONS,
      hidden,
      annotationId,
      storeType
    });
  };
  useEffect(() => {
    const {
      removeHotKeys,
      errors
    } = registerHotKeys([{
      keys: ['s'],
      onAction: () => {
        dispatch({
          type: 'SELECT_ANNOTATION_LABEL',
          annotationLabel: null
        });
      }
    }]);
    if (errors) {
      for (const error of errors) {
        console.error(error);
      }
    }
    return () => {
      removeHotKeys();
    };
  }, [dispatch, registerHotKeys]);
  useHideShortcutKeyHandlers({
    registerHotKeys,
    selectedAnnotationIds,
    annotationList: selectedAnnotations,
    toggleShowHideAnnotations
  });
  useEffect(() => {
    const labelSelectShortcuts = annotationLabels.slice(0, 9).map((label, index) => ({
      // Registers shortcut for number keys 1-9 for selecting the first nine labels.
      keys: [`${index + 1}`],
      onAction: () => dispatch({
        type: 'SELECT_ANNOTATION_LABEL',
        annotationLabel: label
      })
    }));
    if (onOpenTaskQuestionnaire && annotationLabels.length < 9) {
      labelSelectShortcuts.push({
        keys: [`${annotationLabels.length + 1}`],
        onAction: () => onOpenTaskQuestionnaire()
      });
    }
    const {
      errors,
      removeHotKeys
    } = registerHotKeys(labelSelectShortcuts);
    errors?.forEach(handleShortcutRegistrationError);
    return () => {
      removeHotKeys();
    };
  }, [annotationLabels, dispatch, registerHotKeys, onOpenTaskQuestionnaire]);
};